import React, { useEffect, useState } from 'react';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import './QuickAudit.css';
import AddressDisplay from '../addressdisplay/AddressDisplay';
import { AiOutlineCheck, AiOutlineClose  } from "react-icons/ai";
import { MdOutlineInfo } from "react-icons/md";

const Info = MdOutlineInfo as unknown as React.FC<React.SVGProps<SVGSVGElement>>;
const CheckIcon = AiOutlineCheck as unknown as React.FC<React.SVGProps<SVGSVGElement>>;
const TimesIcon = AiOutlineClose as unknown as React.FC<React.SVGProps<SVGSVGElement>>;

interface QuickAuditProps {
  data: any;
  chain: any;
}

const QuickAudit: React.FC<QuickAuditProps> = ({ data, chain }) => {
  const [scannerD, setScanner] = useState<any>(null);

  useEffect(() => {
    if (!data) return;
    const entries = Object.values(data);
    if (!entries.length) return;
    const firstScanner = entries[0];
    if (firstScanner) {
      setScanner(firstScanner);
    }
  }, [data, chain]);

  const getStatusIcon = (label: string, value: any) => {
    if (label === "Contract Verified") {
      return value === "Yes" ? (
        <CheckIcon style={{ color: 'rgb(14, 203, 129)', marginLeft: '5px' }} />
      ) : (
        <TimesIcon style={{ color: 'rgb(246, 70, 93)', marginLeft: '5px' }} />
      );
    } else if (label === "Sell Tax" || label === "Buy Tax") {
      const numericValue = parseFloat(String(value).replace("%", ""));
      return numericValue < 25 ? (
        <CheckIcon style={{ color: 'rgb(14, 203, 129)', marginLeft: '5px' }} />
      ) : (
        <TimesIcon style={{ color: 'rgb(246, 70, 93)', marginLeft: '5px' }} />
      );
    } else if (label === "Transfer Fee") {
      const fee = parseFloat(String(value).replace("%", ""));
      return fee <= 10 ? (
        <CheckIcon style={{ color: 'rgb(14, 203, 129)', marginLeft: '5px' }} />
      ) : (
        <TimesIcon style={{ color: 'rgb(246, 70, 93)', marginLeft: '5px' }} />
      );
    } else if (label === "Fee Update Authority") {
      return value === "Revoked" ? (
        <CheckIcon style={{ color: 'rgb(14, 203, 129)', marginLeft: '5px' }} />
      ) : (
        <TimesIcon style={{ color: 'rgb(246, 70, 93)', marginLeft: '5px' }} />
      );
    } else if (label === "Default Transaction Restriction") {
      return value === "None" ? (
        <CheckIcon style={{ color: 'rgb(14, 203, 129)', marginLeft: '5px' }} />
      ) : (
        <TimesIcon style={{ color: 'rgb(246, 70, 93)', marginLeft: '5px' }} />
      );
    } else if (label === "Mint Authority") {
      return value === "Revoked" ? (
        <CheckIcon style={{ color: 'rgb(14, 203, 129)', marginLeft: '5px' }} />
      ) : (
        <TimesIcon style={{ color: 'rgb(246, 70, 93)', marginLeft: '5px' }} />
      );
    } else if (label === "Freeze Authority") {
      return value === "Revoked" ? (
        <CheckIcon style={{ color: 'rgb(14, 203, 129)', marginLeft: '5px' }} />
      ) : (
        <TimesIcon style={{ color: 'rgb(246, 70, 93)', marginLeft: '5px' }} />
      );
    } else if (label === "Metadata Update") {
      return value === "Revoked" ? (
        <CheckIcon style={{ color: 'rgb(14, 203, 129)', marginLeft: '5px' }} />
      ) : (
        <TimesIcon style={{ color: 'rgb(246, 70, 93)', marginLeft: '5px' }} />
      );
    } else if (label === "Closable") {
      return value === "No" ? (
        <CheckIcon style={{ color: 'rgb(14, 203, 129)', marginLeft: '5px' }} />
      ) : (
        <TimesIcon style={{ color: 'rgb(246, 70, 93)', marginLeft: '5px' }} />
      );
    } else if (label === "Honeypot") {
      return value === "No" ? (
        <CheckIcon style={{ color: 'rgb(14, 203, 129)', marginLeft: '5px' }} />
      ) : (
        <TimesIcon style={{ color: 'rgb(246, 70, 93)', marginLeft: '5px' }} />
      );
    } else if (label === "Manipulated Balance") {
      return value === "No" ? (
        <CheckIcon style={{ color: 'rgb(14, 203, 129)', marginLeft: '5px' }} />
      ) : (
        <TimesIcon style={{ color: 'rgb(246, 70, 93)', marginLeft: '5px' }} />
      );
    } else if (label === "External Hooks") {
      return value === "No" ? (
        <CheckIcon style={{ color: 'rgb(14, 203, 129)', marginLeft: '5px' }} />
      ) : (
        <TimesIcon style={{ color: 'rgb(246, 70, 93)', marginLeft: '5px' }} />
      );
    } else if (label === "Owner Address") {
      return value === "Renounced" ? (
        <CheckIcon style={{ color: 'rgb(14, 203, 129)', marginLeft: '5px' }} />
      ) : (
        <TimesIcon style={{ color: 'rgb(246, 70, 93)', marginLeft: '5px' }} />
      );
    } else {
      return value === "No" ? (
        <CheckIcon style={{ color: 'rgb(14, 203, 129)', marginLeft: '5px' }} />
      ) : (
        <TimesIcon style={{ color: 'rgb(246, 70, 93)', marginLeft: '5px' }} />
      );
    }
  };

  let dataMap;

  if (chain === 501424) {
    dataMap = scannerD
      ? [
          {
            label: "Transfer Fee",
            value:
              scannerD.transfer_fee &&
              Object.keys(scannerD.transfer_fee).length > 0
                ? (scannerD.transfer_fee?.current_fee_rate?.fee_rate / 100) + "%"
                : "0%",
            tooltip: "The fee applied during token transfers.",
          },
          {
            label: "Fee Update Authority",
            value: scannerD.transfer_fee_upgradable?.status === "0" ? "Revoked" : "Active",
            tooltip:
              "If the transfer fee can be updated. High fee may result in inability to trade.",
          },
          {
            label: "Mint Authority",
            value: scannerD.mintable?.status === "0" ? "Revoked" : "Active",
            tooltip: "If the token can be minted.",
          },
          {
            label: "Freeze Authority",
            value: scannerD.freezable?.status === "0" ? "Revoked" : "Active",
            tooltip: "If freeze authority found.",
          },
          {
            label: "Metadata Changeable",
            value: scannerD.metadata_mutable?.status === "0" ? "No" : "Yes",
            tooltip: "If the metadata can be changed",
          },
          {
            label: "Closable",
            value: scannerD.closable?.status === "0" ? "No" : "Yes",
            tooltip: "If the token program can be closed.",
          },
          {
            label: "Honeypot",
            value: scannerD.non_transferable === "0" ? "No" : "Yes",
            tooltip: "The token is transferable after purchase.",
          },
          {
            label: "Manipulated Balance",
            value:
              scannerD.balance_mutable_authority?.status === "0" ? "No" : "Yes",
            tooltip: "If the user balance can be Manipulated.",
          },
          {
            label: "External Hooks",
            value:
              scannerD.transfer_hook &&
              scannerD.transfer_hook.length === 0 &&
              scannerD.transfer_hook_upgradable?.status === "0"
                ? "No"
                : "Yes",
            tooltip: "If the contract has external hooks.",
          },
        ]
      : [];
  } else {
    dataMap = scannerD
      ? [
          {
            label: "Contract Verified",
            value: scannerD.is_open_source === "1" ? "Yes" : "No",
            tooltip: "Indicates if the contract code is verified as open source.",
          },
          {
            label: "Honeypot",
            value: scannerD.is_honeypot === "1" ? "Yes" : "No",
            tooltip: "Indicates if the contract is a honeypot.",
          },
          {
            label: "Buy Tax",
            value: ((scannerD?.buy_tax && scannerD.buy_tax.toString().trim() !== "") ? scannerD.buy_tax * 100 : 0) + "%",
            tooltip: "The tax rate applied when buying tokens.",
          },
          {
            label: "Sell Tax",
            value: ((scannerD?.sell_tax && scannerD.sell_tax.toString().trim() !== "") ? scannerD.sell_tax * 100 : 0) + "%",
            tooltip: "The tax rate applied when selling tokens.",
          },
          {
            label: "Proxy Contract",
            value: scannerD.is_proxy === "1" ? "Yes" : "No",
            tooltip: "Indicates if the contract is a proxy contract.",
          },
          {
            label: "Mintable",
            value: scannerD.is_mintable === "1" ? "Yes" : "No",
            tooltip: "Indicates if new tokens can be minted.",
          },
          {
            label: "Max Sell Ratio",
            value: scannerD.cannot_sell_all === "1" ? "Yes" : "No",
            tooltip: "Indicates if there is a maximum sell ratio enforced.",
          },
          {
            label: "Paused",
            value: scannerD.transfer_pausable === "1" ? "Yes" : "No",
            tooltip: "Indicates if token transfers can be paused.",
          },
          {
            label: "Blacklisted",
            value: scannerD.is_blacklisted === "1" ? "Yes" : "No",
            tooltip: "Indicates if the contract is blacklisted.",
          },
          {
            label: "Antiwhale Modifiable",
            value: scannerD.anti_whale_modifiable === "1" ? "Yes" : "No",
            tooltip: "Indicates if antiwhale settings can be modified.",
          },
          {
            label: "Trading Cooldown",
            value: scannerD.trading_cooldown === "1" ? "Yes" : "No",
            tooltip: "Indicates if there is a trading cooldown period.",
          },
          {
            label: "Owner Address",
            value: (
              scannerD?.owner_address === "0x0000000000000000000000000000000000000000" || scannerD?.owner_address === "" || !scannerD?.owner_address || scannerD?.owner_address === "0x000000000000000000000000000000000000dead"
                ? "Renounced"
                : <><AddressDisplay address={scannerD.owner_address} />  <TimesIcon style={{ color: 'rgb(246, 70, 93)', marginLeft: '5px' }} /></>
            ),
            tooltip: "The owner's address.",
          },
        ]
      : [];
  }

  return (
    <>
      <div className='token-security'>
        {scannerD ? (
          dataMap.map((item, index) => (
            <div key={index} className="scanner-data">
              <div data-tooltip-id="global-tooltip" data-tooltip-content={item.tooltip}>
                <Info /> {item.label}
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {item.value}
                {typeof item.value === 'string' && getStatusIcon(item.label, item.value)}
              </div>
            </div>
          ))
        ) : (
          "Loading..."
        )}
      </div>
      <Tooltip id="global-tooltip" className="my-tooltip" />
    </>
  );
};

export default QuickAudit;
